import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Download,
  Github,
  Linkedin,
  Mail,
  Code,
  CloudLightning,
  Database,
  Cpu,
} from "lucide-react";
import { portfolioAPI } from "../../services/api";
import { HomeSkeleton } from "../../components/Skeleton/PageSkeletons";

const HomeContainer = styled.div`
  padding: 80px 24px 24px;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 0;
  text-align: center;
`;

const HeroContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 32px;
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 16px;
  line-height: 1.3;

  .highlight {
    color: ${props => props.theme.colors.primary};
    font-weight: 500;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 20px;
  font-weight: 400;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 24px;
  line-height: 1.4;
`;

const HeroDescription = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.hover};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StatsSection = styled.section`
  padding: 60px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 60px;
`;

const StatCard = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 32px 24px;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${props => props.theme.shadows.dropdown};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatNumber = styled.h3`
  font-size: 24px;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const FeaturesSection = styled.section`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 40px;
  margin-bottom: 40px;
`;

const FeaturesHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const FeaturesTitle = styled.h2`
  font-size: 24px;
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 12px;
`;

const FeaturesDescription = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
`;

const FeatureCard = styled.div`
  text-align: center;

  .feature-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto 16px;
    background-color: ${props => props.theme.colors.surfaceSecondary};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.small};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.primary};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.5;
    margin: 0;
  }
`;

const Home = () => {
  const [personalData, setPersonalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const stats = [
    { icon: Code, number: "20+", label: "Major Projects" },
    { icon: CloudLightning, number: "3+", label: "Years Experience" },
    { icon: Database, number: "15+", label: "Technologies" },
    { icon: Cpu, number: "$1M+", label: "Cost Savings" },
  ];

  const features = [
    {
      icon: CloudLightning,
      title: "Cloud-Native Development",
      description:
        "Expertise in AWS services, serverless architecture, and scalable cloud solutions.",
    },
    {
      icon: Database,
      title: "Full-Stack Engineering",
      description:
        "Proficient in Node.js, Python, React.js, and modern database technologies.",
    },
    {
      icon: Cpu,
      title: "AI/ML Integration",
      description:
        "Experience with LLM/RAG systems, vector databases, and AI-powered applications.",
    },
  ];

  useEffect(() => {
    const fetchPersonalData = async () => {
      try {
        setLoading(true);
        const response = await portfolioAPI.getPersonal();
        setPersonalData(response.data.data);
      } catch (error) {
        console.error("Error fetching personal data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalData();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const socialLinks = [
    { href: "https://github.com/hassan8537", icon: Github },
    {
      href: "https://www.linkedin.com/in/m-hassan-ahmed-976903220",
      icon: Linkedin,
    },
    { href: "mailto:hassan.m.8537@gmail.com", icon: Mail },
  ];

  if (loading) {
    return <HomeSkeleton />;
  }

  return (
    <HomeContainer>
      <Content>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <HeroSection>
            <HeroContent variants={itemVariants}>
              <HeroTitle>
                Hi, I'm{" "}
                <span className="highlight">
                  {personalData?.name || "M Hassan Ahmed"}
                </span>
              </HeroTitle>

              <HeroSubtitle>
                {personalData?.title ||
                  "Software Engineer | Cloud-Native Developer"}
              </HeroSubtitle>

              <HeroDescription>
                {personalData?.bio
                  ? personalData.bio.split(".").slice(0, 2).join(".") + "."
                  : "Software Engineer with expertise in Node.js, Python, and cloud-native development. Proven track record building scalable real-time applications, RESTful APIs, and serverless solutions."}
              </HeroDescription>

              <ButtonGroup>
                <Link to="/projects" className="aws-button primary">
                  <ArrowRight />
                  View My Work
                </Link>
                <a
                  href="/M-Hassan-Ahmed-Resume.pdf"
                  download
                  className="aws-button secondary"
                >
                  <Download />
                  Download Resume
                </a>
              </ButtonGroup>

              <SocialLinks>
                {socialLinks.map(({ href, icon: Icon }, index) => (
                  <SocialLink
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon />
                  </SocialLink>
                ))}
              </SocialLinks>
            </HeroContent>
          </HeroSection>

          <StatsSection>
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatCard
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <StatIcon>
                    <stat.icon />
                  </StatIcon>
                  <StatNumber>{stat.number}</StatNumber>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCard>
              ))}
            </StatsGrid>

            <motion.div variants={itemVariants}>
              <FeaturesSection>
                <FeaturesHeader>
                  <FeaturesTitle>Core Expertise</FeaturesTitle>
                  <FeaturesDescription>
                    Specialized in delivering scalable, cloud-native solutions
                    with modern technologies
                  </FeaturesDescription>
                </FeaturesHeader>

                <FeaturesGrid>
                  {features.map((feature, index) => (
                    <FeatureCard key={index}>
                      <div className="feature-icon">
                        <feature.icon />
                      </div>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </FeatureCard>
                  ))}
                </FeaturesGrid>
              </FeaturesSection>
            </motion.div>
          </StatsSection>
        </motion.div>
      </Content>
    </HomeContainer>
  );
};

export default Home;
